import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import { useSelector } from "react-redux";
import YhInput from "../../../../../ui/YhInput";
import YhButton from "../../../../../ui/YhButton";
import CustomLoader from "../../../../../global/components/CustomLoader/CustomLoader";
import { useStepper } from "../../../../BulkUpload/components/exportLead/StepperContext";
import { ImageUpLoad } from "../EmployeeDocuments/EmployeeDocumentServices";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../../helpers/methods";
import notifiers from "../../../../../global/constants/NotificationConstants";
import { validateInput } from "./ValidatorOffice";
import { updateExportDetails } from "../StepperService";
import SourceSubSourceFields, {
  SourceType,
} from "../../../../../Shared/SourceSubSourceFields";
import { selectActiveOrganizationId } from "../../../../../redux/authSlice";

const OfficialInformation = (props: any) => {
  const { updateStepperProps } = useStepper();
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const organizationId =
    activeOrgId && activeOrgId !== "all" && activeOrgId !== "*"
      ? String(activeOrgId)
      : "";
  const fields = [
    "lead_name",
    "lead_email",
    "lead_phone",
    "lead_source",
    "lead_project",
  ];
  const [apiResponse, setApiResponse] = useState(false);
  const [file, setFile] = useState<any>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileData, setFileData] = useState<any[]>([]);
  const [personalDetailState, setPersonalDetailState] = useState<any>({
    fileName: "",
  });
  const [sourceType, setSourceType] = useState<SourceType>("bulk");
  const [subSource, setSubSource] = useState("");

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);

    Papa.parse(selectedFile, {
      header: true,
      complete: (result: any) => {
        const csvHeaders = result.meta.fields || [];
        const csvData = result.data || [];
        setFileData(
          csvData.filter((item: any) => Object.keys(item)?.length > 2)
        );
        setHeaders(csvHeaders);
      },
      error: (error: any) => {
        openErrorNotification(error?.message || "CSV parsing failed");
      },
    });
  };

  const submitForm = async () => {
    if (!file) {
      openErrorNotification("Please upload a CSV file");
      return;
    }
    if (!personalDetailState?.fileName?.trim()) {
      openErrorNotification("Please enter a file name");
      return;
    }
    if (!subSource.trim()) {
      openErrorNotification("Select or create a sub-source");
      return;
    }

    const isOk = headers.map((item: any) => fields.includes(item));
    if (isOk.includes(false) || !headers.length) {
      openErrorNotification("Header value are not match");
      return;
    }

    const data = new FormData();
    data.append("file", file);

    try {
      setApiResponse(true);
      const bo = {
        upload_file_name: personalDetailState.fileName,
      };
      const uploadeName = await updateExportDetails(bo);
      const uploadedUrl = await ImageUpLoad(data);
      const withSource = (fileData || []).map((row: any) => ({
        ...row,
        lead_source: subSource.trim(),
      }));

      updateStepperProps({
        headers,
        imagePath: uploadedUrl,
        fileData: withSource,
        fileName: file.name,
        uploadLeadId: uploadeName?.data?._id,
        personalDetailState,
        sourceType,
        subSource: subSource.trim(),
      });
      props.nextStep?.();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setApiResponse(false);
    }
  };

  useEffect(() => {
    props.registerStepActions?.({ submit: submitForm });
  });

  const handleOnChangeInputField = (event: any) => {
    const name: any = event.target.name;
    const value: any = event.target.value;
    const isGoForward: any = validateInput(name, value);
    if (!isGoForward) return;
    setPersonalDetailState((prevFormData: any) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleDownload = () => {
    const csv = convertToCSV([], fields);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const convertToCSV = (data: any, csvFields: any) => {
    const csv = [];
    csv.push(csvFields.join(","));
    data.forEach((row: any) => {
      const values = csvFields.map((field: any) => row[field]);
      csv.push(values.join(","));
    });
    return csv.join("\n");
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-semibold text-[#111827]">Lead Details</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Upload a CSV using the sample template headers.
        </p>
      </div>

      <SourceSubSourceFields
        organizationId={organizationId}
        sourceType={sourceType}
        subSource={subSource}
        onSourceTypeChange={setSourceType}
        onSubSourceChange={setSubSource}
      />

      <div className="flex flex-wrap items-center gap-3">
        <YhButton variant="secondary" size="sm" onClick={handleDownload}>
          Download Sample Template
        </YhButton>
        {file ? (
          <span className="text-sm text-[#374151]">{file.name}</span>
        ) : null}
        {headers.length ? (
          <button
            type="button"
            className="text-sm font-semibold text-[#DC2626] hover:underline"
            onClick={() => {
              setHeaders([]);
              setFile(null);
              setFileData([]);
            }}
          >
            Remove
          </button>
        ) : null}
      </div>

      <div className="grid max-w-xl gap-4 sm:grid-cols-2">
        <div>
          <p className="yh-label mb-1">CSV File</p>
          <input
            accept=".csv"
            id="bulk-projectwise-csv"
            type="file"
            onChange={handleFileChange}
            className="block w-full text-sm text-[#374151] file:mr-3 file:rounded-lg file:border-0 file:bg-[#EEF0FF] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-[#4338CA]"
          />
        </div>
        <YhInput
          label="File Name"
          placeholder="Enter Name"
          value={personalDetailState?.fileName || ""}
          onChange={handleOnChangeInputField}
          name="fileName"
          type="text"
        />
      </div>

      {!props.hideFooterButtons && (
        <div className="mt-4 flex justify-end gap-2">
          <YhButton variant="secondary" size="sm" onClick={props.backStep}>
            Back
          </YhButton>
          <YhButton variant="primary" size="sm" onClick={submitForm}>
            Save & Continue
          </YhButton>
        </div>
      )}

      {apiResponse && <CustomLoader apiResponse={apiResponse} />}
    </div>
  );
};

export default OfficialInformation;
